
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * FeedServerSettings struct
 */
class FeedServerSettings {
  /**
   * Initialize struct
   * @param {!number=} feedPort
   * @param {!string=} latestTickDatabaseFile
   * @param {!boolean=} useSsl
   * @constructor
   */
  constructor (feedPort = 6110, latestTickDatabaseFile = '../history/ticks-latest.db', useSsl = false) {
    this.FeedPort = feedPort
    this.LatestTickDatabaseFile = latestTickDatabaseFile
    this.UseSsl = useSsl
  }

  /**
   * Copy struct (shallow copy)
   * @this {!FeedServerSettings}
   * @param {!FeedServerSettings} other Other struct
   * @returns {!FeedServerSettings} This struct
   */
  copy (other) {
    if (other.FeedPort != null) {
      this.FeedPort = other.FeedPort
    } else {
      this.FeedPort = null
    }
    if (other.LatestTickDatabaseFile != null) {
      this.LatestTickDatabaseFile = other.LatestTickDatabaseFile
    } else {
      this.LatestTickDatabaseFile = null
    }
    if (other.UseSsl != null) {
      this.UseSsl = other.UseSsl
    } else {
      this.UseSsl = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!FeedServerSettings}
   * @returns {!FeedServerSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new FeedServerSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new FeedServerSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!FeedServerSettings}
   * @param {!FeedServerSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof FeedServerSettings)) {
      throw new TypeError('Instance of FeedServerSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!FeedServerSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      FeedPort: ((this.FeedPort != null) ? this.FeedPort : null),
      LatestTickDatabaseFile: ((this.LatestTickDatabaseFile != null) ? this.LatestTickDatabaseFile : null),
      UseSsl: ((this.UseSsl != null) ? this.UseSsl : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return FeedServerSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!FeedServerSettings} other Object value
   * @returns {!FeedServerSettings} Created struct
   */
  static fromObject (other) {
    return new FeedServerSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!FeedServerSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return FeedServerSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!FeedServerSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 9
  }
}

export { FeedServerSettings };

/**
 * Fast Binary Encoding FeedServerSettings field model
 */
class FieldModelFeedServerSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.FeedPort = new fbe.FieldModelUInt16(buffer, 4 + 4)
    this.LatestTickDatabaseFile = new fbe.FieldModelString(buffer, this.FeedPort.fbeOffset + this.FeedPort.fbeSize)
    this.UseSsl = new fbe.FieldModelBool(buffer, this.LatestTickDatabaseFile.fbeOffset + this.LatestTickDatabaseFile.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelFeedServerSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelFeedServerSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.FeedPort.fbeSize + this.LatestTickDatabaseFile.fbeSize + this.UseSsl.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelFeedServerSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.FeedPort.fbeExtra + this.LatestTickDatabaseFile.fbeExtra + this.UseSsl.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelFeedServerSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelFeedServerSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelFeedServerSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 9
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelFeedServerSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelFeedServerSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.FeedPort.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FeedPort.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FeedPort.fbeSize

    if ((fbeCurrentSize + this.LatestTickDatabaseFile.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LatestTickDatabaseFile.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LatestTickDatabaseFile.fbeSize

    if ((fbeCurrentSize + this.UseSsl.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UseSsl.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSsl.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelFeedServerSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelFeedServerSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelFeedServerSettings}
   * @param {!FeedServerSettings} fbeValue Default value, defaults is new FeedServerSettings()
   * @returns {!FeedServerSettings} FeedServerSettings value
   */
  get (fbeValue = new FeedServerSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelFeedServerSettings}
   * @param {!FeedServerSettings} fbeValue FeedServerSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.FeedPort.fbeSize) <= fbeStructSize) {
      fbeValue.FeedPort = this.FeedPort.get(6110)
    } else {
      fbeValue.FeedPort = 6110
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FeedPort.fbeSize

    if ((fbeCurrentSize + this.LatestTickDatabaseFile.fbeSize) <= fbeStructSize) {
      fbeValue.LatestTickDatabaseFile = this.LatestTickDatabaseFile.get('../history/ticks-latest.db')
    } else {
      fbeValue.LatestTickDatabaseFile = '../history/ticks-latest.db'
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LatestTickDatabaseFile.fbeSize

    if ((fbeCurrentSize + this.UseSsl.fbeSize) <= fbeStructSize) {
      fbeValue.UseSsl = this.UseSsl.get(false)
    } else {
      fbeValue.UseSsl = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSsl.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelFeedServerSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelFeedServerSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelFeedServerSettings}
   * @param {!FeedServerSettings} fbeValue FeedServerSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelFeedServerSettings}
   * @param {FeedServerSettings} fbeValue FeedServerSettings value
   */
  setFields (fbeValue) {
    this.FeedPort.set(fbeValue.FeedPort)
    this.LatestTickDatabaseFile.set(fbeValue.LatestTickDatabaseFile)
    this.UseSsl.set(fbeValue.UseSsl)
  }
}

export { FieldModelFeedServerSettings };

/**
 * Fast Binary Encoding FeedServerSettings model
 */
class FeedServerSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelFeedServerSettings(this.buffer, 4)
  }

  /**
   * Get the FeedServerSettings model
   * @this {!FeedServerSettingsModel}
   * @returns {!FieldModelFeedServerSettings} model FeedServerSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!FeedServerSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!FeedServerSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return FeedServerSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!FeedServerSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelFeedServerSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!FeedServerSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!FeedServerSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!FeedServerSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!FeedServerSettingsModel}
   * @param {!FeedServerSettings} value FeedServerSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!FeedServerSettingsModel}
   * @param {!FeedServerSettings} value FeedServerSettings value, defaults is new FeedServerSettings()
   * @return {!object} Deserialized FeedServerSettings value and its size
   */
  deserialize (value = new FeedServerSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new FeedServerSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new FeedServerSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!FeedServerSettingsModel}
   * @param {!number} prev Previous FeedServerSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { FeedServerSettingsModel };
